/**
 * Display Mode Strategies
 *
 * Reusable strategies for popup display modes.
 * Each strategy handles the display-mode-specific behavior
 * for popups (standard, banner, fullscreen, corner).
 */

import type { DisplayConfig } from '../../shared/types/popup';
import type { DisplayModeStrategy } from './types';
import { StandardModeStrategy } from './standard-mode';
import { BannerModeStrategy } from './banner-mode';
import { FullscreenModeStrategy } from './fullscreen-mode';
import { CornerModeStrategy } from './corner-mode';

/**
 * Factory function to create the appropriate display mode strategy
 * based on the display configuration.
 *
 * @param config The display configuration from the popup config
 * @return The appropriate DisplayModeStrategy instance
 */
export function createDisplayModeStrategy(
	config: DisplayConfig
): DisplayModeStrategy {
	switch (config.displayMode) {
		case 'banner':
			return new BannerModeStrategy(
				config.bannerPosition || 'top',
				config.animationType
			);
		case 'fullscreen':
			return new FullscreenModeStrategy();
		case 'corner':
			return new CornerModeStrategy(config.cornerPosition);
		case 'popup':
		default:
			return new StandardModeStrategy();
	}
}

// Export types
export type { DisplayModeStrategy } from './types';

// Export strategy classes for direct use if needed
export { StandardModeStrategy } from './standard-mode';
export { BannerModeStrategy } from './banner-mode';
export { FullscreenModeStrategy } from './fullscreen-mode';
export { CornerModeStrategy } from './corner-mode';
